import React, { Component } from 'react';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { dismissVerificationPanel, refreshVerificationStatus } from '../../../actions/auth';
import ResendVerificationButton from './ResendVerificationButton';

/**
 * Build-time flag deciding which of the two banner sentences is true.
 *
 * While the backend's `EMAIL_VERIFICATION_MODE` is `off` — the shipped default
 * and the client's current decision — "verify it to place orders" is a lie, and
 * a storefront must not lie to its customers. The soft variant is the default;
 * the hard one requires whoever turned the backend gate on to set this too.
 */
const ENFORCED = process.env.REACT_APP_EMAIL_VERIFICATION_ENFORCED === 'true';

/**
 * The persistent, DISMISSIBLE affordance for a returning unverified customer.
 *
 * A banner, not a modal and not an interstitial: an unverified address is a
 * standing housekeeping item, not an emergency, and blocking a browsing
 * customer over it would cost more than it buys. Dismissal is session-scoped —
 * it is redux state and is not persisted — so it returns next visit.
 *
 * IT RENDERS FROM THE SERVER'S ANSWER, NEVER FROM FIREBASE. The input is
 * `state.prof.profile.email_verified`, which the backend put there
 * (`controllers/_v3/auth/index.js`). Reading `auth.currentUser.emailVerified`
 * locally would give the UI a second, disagreeing source of truth and the two
 * would visibly fight.
 */
class EmailVerificationBanner extends Component {

    constructor(props) {
        super(props);
        this.state = { checking: false, justVerified: false };
        this.onCheckAgain = this.onCheckAgain.bind(this);
    }

    componentWillUnmount() {
        this.unmounted = true;
    }

    /**
     * The "I've verified" affordance. Forces `reload()` + `getIdToken(true)` and
     * re-asks the backend — this is the whole fix for token staleness, and the
     * difference between the feature working and the site reading as broken.
     * @returns {Promise<void>}
     */
    async onCheckAgain() {
        this.setState({ checking: true });
        const verified = await this.props.refreshVerificationStatus();
        if (this.unmounted) return;
        this.setState({ checking: false, justVerified: verified });
    }

    render() {
        const { translate, profile, panelDismissed, emailSentTo } = this.props;
        const { checking, justVerified } = this.state;

        // The post-signup panel already says all of this, better. Do not stack them.
        if (!profile || emailSentTo) return null;
        if (profile.email_verified !== false) return null;
        if (panelDismissed) return null;

        if (justVerified) {
            return (
                <div className="email-verification-banner email-verification-banner--ok" role="status">
                    <FontAwesomeIcon icon={['fas', 'check-circle']} />
                    <span>{translate('EMAIL_VERIFICATION.VERIFIED_TOAST')}</span>
                </div>
            );
        }

        return (
            <div className="email-verification-banner" role="status">
                <span className="email-verification-banner__text">
                    {translate(ENFORCED ? 'EMAIL_VERIFICATION.BANNER_ENFORCED' : 'EMAIL_VERIFICATION.BANNER_SOFT')}
                </span>
                <div className="email-verification-banner__actions">
                    <ResendVerificationButton className="btn-sm" />
                    <button
                        type="button"
                        className="btn btn-link btn-sm"
                        disabled={checking}
                        onClick={this.onCheckAgain}
                    >
                        {checking
                            ? translate('EMAIL_VERIFICATION.GATE_CHECKING')
                            : translate('EMAIL_VERIFICATION.CHECK_AGAIN')}
                    </button>
                    <button
                        type="button"
                        className="email-verification-banner__dismiss"
                        aria-label={translate('EMAIL_VERIFICATION.DISMISS')}
                        onClick={this.props.dismissVerificationPanel}
                    >
                        <FontAwesomeIcon icon={['fas', 'times']} />
                    </button>
                </div>
            </div>
        );
    }
}

/**
 * @param {object} state - Redux state.
 * @returns {object} The backend's profile plus banner UI state and the translator.
 */
function mapStateToProps(state) {
    return {
        translate: getTranslate(state.locale),
        profile: state.prof.profile,
        panelDismissed: state.auth.verification.panelDismissed,
        emailSentTo: state.auth.verification.emailSentTo,
    };
}

export default connect(mapStateToProps, { dismissVerificationPanel, refreshVerificationStatus })(EmailVerificationBanner);
